import { forwardRef, type HTMLAttributes } from 'react';

import {
  GLASS_CARD_SHELL,
  glassWashClass,
  type GlassWashTone,
} from '@/shared/ui-class';
import { cn } from '@/shared/cn';

/**
 * Glass-surface card shell — `rounded-2xl + glass-border + glass-surface +
 * backdrop-blur-lg`. The most-duplicated visual stack in the project
 * (~16 sites). Tone picks the surface fill: the default soft `--glass-surface`
 * for list/form containers, `strong` for callout / hero surfaces that need
 * a touch more contrast. `warning` / `danger` / `info` add the canonical
 * corner gradient wash (docs/design-philosophy.md § Glass vocabulary) while
 * keeping the neutral hairline border.
 *
 * The intentionally-left-out variants are pre-auth Fiber paper (`AuthSplitShell`
 * on `/login` + `/signup`), the ChatErrorBoundary
 * fallback (page-level, special shadow), and the composer in Thread.tsx
 * (complex inner structure). Those keep their bespoke stacks.
 */
export type GlassCardTone = 'default' | 'strong' | GlassWashTone;

export type GlassCardProps = HTMLAttributes<HTMLDivElement> & {
  tone?: GlassCardTone;
  /** Override the element used for semantic landmarks (default `<div>`). */
  as?: 'div' | 'section' | 'nav' | 'aside' | 'article';
};

function glassCardFillClass(tone: GlassCardTone): string {
  if (tone === 'strong') {
    return 'bg-[var(--glass-surface-strong)]';
  }
  return 'bg-[var(--glass-surface)]';
}

export const GlassCard = forwardRef<HTMLDivElement, GlassCardProps>(
  function GlassCard(
    { className, tone = 'default', as = 'div', children, ...rest },
    ref,
  ) {
    const Component = as;
    const washTone: GlassWashTone =
      tone === 'warning' || tone === 'danger' || tone === 'info'
        ? tone
        : 'default';

    return (
      <Component
        ref={ref}
        className={cn(
          GLASS_CARD_SHELL,
          glassCardFillClass(tone),
          glassWashClass(washTone),
          className,
        )}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);
